/**
 * Слежение за микрофоном во время записи (12.09.2026).
 *
 * 🔴 ЗАЧЕМ. Разбор 12.09.2026 (`docs/CHECK-2026-09-12-RECORD-TRUNC.md` §3.3 п. 2):
 * рекордер не слушал ни одного события дорожки. Микрофон забирало другое
 * приложение, отключалась гарнитура, дорожка кончалась — а часы на экране шли
 * дальше, ученик говорил в пустоту и отправлял обрывок. Потерю надо назвать в ту
 * же секунду, когда она случилась, а не через две минуты речи.
 *
 * ⚠️ О ПОТЕРЕ СООБЩАЕМ ОДИН РАЗ. Отключение гарнитуры даёт подряд `mute`,
 * `ended` и `devicechange`; три красные плашки на одно событие — шум, из которого
 * ученик ничего не поймёт. Важна первая причина: она ближе всего к тому, что
 * произошло на самом деле.
 *
 * ⚠️ `devicechange` САМ ПО СЕБЕ — НЕ ПОТЕРЯ. Воткнули наушники, а запись идёт с
 * того же микрофона — пугать незачем. Он лишь повод перечитать состояние дорожки:
 * часть браузеров не шлёт по ней `ended` вовсе.
 */

/**
 * Сколько ждать возвращения замолчавшего микрофона, прежде чем обрывать запись.
 *
 * 🔴 ЗАМЕРЕНО НА ЖИВОМ iPhone (iOS 18.7, Safari 26.6.1, 12.09.2026): при уходе в
 * другое приложение приходит `mute`, и дальше в файл не попадает ничего — даже
 * после возвращения. Из 30 секунд по часам в файле осталось 8. Ждать дольше
 * значит дать ученику говорить в мёртвый рекордер.
 */
export const MUTE_GRACE_MS = 2000;

export type MicLossKind = 'ended' | 'mute' | 'error';
export type MicLoss = { kind: MicLossKind; atMs: number };

/** Ровно то, чем пользуется слежение, — чтобы проверять без браузера. */
type Listenable = {
  addEventListener?(type: string, fn: () => void): void;
  removeEventListener?(type: string, fn: () => void): void;
};

/**
 * ⚠️ ПОДПИСЫВАЕМСЯ ОСТОРОЖНО. Не у всякого объекта, который приходит сюда, есть
 * `addEventListener`: у части браузеров его нет у `mediaDevices`, а в проверках
 * поток и вовсе поддельный. Брошенное здесь исключение поднялось бы до `catch`
 * вокруг запуска записи, и ученик увидел бы «нет доступа к микрофону» на ровном
 * месте — то есть слежение, заведённое ради надёжности, саму запись и сломало бы.
 */
function on(target: Listenable | undefined, type: string, fn: () => void): void {
  if (typeof target?.addEventListener === 'function') target.addEventListener(type, fn);
}
function off(target: Listenable | undefined, type: string, fn: () => void): void {
  if (typeof target?.removeEventListener === 'function') target.removeEventListener(type, fn);
}
type WatchedTrack = Listenable & { readyState?: string; muted?: boolean };
/**
 * Только гнездо `onerror`. Тип у настоящего `MediaRecorder` уже нашего
 * (`ErrorEvent`), и описать его строже значило бы либо приводить рекордер в
 * месте вызова, либо повторять здесь типы DOM ради одного присваивания.
 */
type WatchedRecorder = { onerror?: unknown };

export type MicWatch = {
  tracks: WatchedTrack[];
  recorder: WatchedRecorder;
  /** `navigator.mediaDevices`; нет — значит и следить не за чем. */
  devices?: Listenable;
  /** Сколько записано к этому мгновению — чтобы сказать «отключился на 0:20». */
  elapsedMs: () => number;
  onLoss: (loss: MicLoss) => void;
  /** Микрофон вернулся: часы можно продолжить, но записанное уже с дырой. */
  onBack?: () => void;
};

export function watchMicrophone(watch: MicWatch): () => void {
  const { tracks, recorder, devices, elapsedMs, onLoss, onBack } = watch;
  let done = false;
  let told = false;

  const lose = (kind: MicLossKind) => {
    if (done || told) return;
    told = true;
    onLoss({ kind, atMs: elapsedMs() });
  };

  const onEnded = () => lose('ended');
  const onMute = () => lose('mute');
  const onUnmute = () => {
    if (done) return;
    // ⚠️ Микрофон вернулся — значит следующая потеря будет НОВОЙ, и о ней надо
    // сказать снова. Иначе «замолчал и вернулся» посреди записи навсегда затыкал
    // бы нам рот про настоящий обрыв в конце.
    told = false;
    onBack?.();
  };
  // Список устройств поменялся — смотрим, жива ли НАША дорожка.
  const onDeviceChange = () => {
    if (done) return;
    for (const track of tracks) {
      if (track.readyState === 'ended') return lose('ended');
      if (track.muted === true) return lose('mute');
    }
  };

  for (const track of tracks) {
    on(track, 'ended', onEnded);
    on(track, 'mute', onMute);
    on(track, 'unmute', onUnmute);
  }
  on(devices, 'devicechange', onDeviceChange);

  const slot = recorder as { onerror?: unknown };
  const previousError = (slot.onerror as ((e: unknown) => void) | null | undefined) ?? null;
  slot.onerror = (e: unknown) => {
    lose('error');
    previousError?.(e);
  };

  return () => {
    if (done) return;
    done = true;
    for (const track of tracks) {
      off(track, 'ended', onEnded);
      off(track, 'mute', onMute);
      off(track, 'unmute', onUnmute);
    }
    off(devices, 'devicechange', onDeviceChange);
    slot.onerror = previousError;
  };
}
